import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Breadcrumbs from "../based/Breadcrumbs";
import CommonTable from "../based/CommonTable";
import { IMAGEABLE_TYPE, INITPAGING, NOTIFY } from "../based/Constants";
import { ConfirmDialog } from "../based/Dialog";
import CheckBox from "../based/inputs/CheckBox";
import Loading from "../based/Loading";
import { Notify } from "../based/Notify";
import CustomizeServices from "../based/services/CustomizeServices";
import CusStockphotoServices from "../based/services/CusStockphotoServices";
import StockFolderServices from "../based/services/StockFolderServices";
import AddOrEditStockModal from "./AddOrEditStockModal";
import ManyAddStockModal from "./ManyAddStockModal";
import CommonModal from "../based/Modal";
import Request from "../based/Request";
import EmbroideryPatternList from "../embroidery-pattern/EmbroideryPatternList";

const StockPhotoModel = {
    id: 0,
    title: "",
    description: "",
    thumbUrl: "",
    width: 0,
    height: 0,
    tagConnections: [],
};
// Page tabs decide which API and table schema the Stock Photo page should use.
const StockPhotoPageTab = {
    StockPhoto: "stockPhoto",
    CustomizeFrame: "customizeFrame",
    EmbroideryPattern: "embroideryPattern",
};
// FrameUploadModel stores the temporary URLs and SVG checks used by the upload modal.
const DEFAULT_FRAME_MASK_TYPE = "rect";
const FrameUploadModel = {
    name: "",
    description: "",
    overlayUrl: "",
    maskSvgUrl: "",
    overlayFileName: "",
    maskFileName: "",
    assetGuid: "",
    maskType: DEFAULT_FRAME_MASK_TYPE,
    maskPath: "",
    maskX: null,
    maskY: null,
    maskWidth: null,
    maskHeight: null,
    maskXRate: null,
    maskYRate: null,
    maskWidthRate: null,
    maskHeightRate: null,
    overlayAnalysis: null,
    maskAnalysis: null,
};

const CustomizeFrameAssetModel = {
    id: 0,
    assetGuid: null,
    name: "",
    description: "",
    thumbUrl: "",
    overlayUrl: "",
    blobUrl: "",
    viewBoxX: 0,
    viewBoxY: 0,
    viewBoxWidth: 0,
    viewBoxHeight: 0,
    maskType: DEFAULT_FRAME_MASK_TYPE,
    maskX: null,
    maskY: null,
    maskWidth: null,
    maskHeight: null,
    maskXRate: null,
    maskYRate: null,
    maskWidthRate: null,
    maskHeightRate: null,
    maskPath: "",
    maskSvgUrl: "",
    isActive: true,
    cusImageId: 0,
};

const requiredFrameNumberFields = ["viewBoxX", "viewBoxY", "viewBoxWidth", "viewBoxHeight"];
const nullableFrameNumberFields = ["maskX", "maskY", "maskWidth", "maskHeight", "maskXRate", "maskYRate", "maskWidthRate", "maskHeightRate"];

// Empty value helper keeps frame validation independent from other admin utilities.
const isEmptyValue = (value) => value === undefined || value === null || value.toString().trim() === "";
const createFrameAssetGuid = () => {
    // AssetGuid is generated client-side so each saved frame has a stable external identifier.
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
        const random = (Math.random() * 16) | 0;
        const value = char === "x" ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
};

const getNullableNumber = (value) => {
    // API decimal fields should receive null instead of empty strings from optional SVG metadata.
    if (value === undefined || value === null || value === "") return null;
    const number = Number(value);
    return Number.isNaN(number) ? null : number;
};

const getRequiredNumber = (value) => getNullableNumber(value) ?? 0;

const getSvgNumberAttribute = (node, name) => {
    // SVG numeric attributes are normalized before being sent to decimal DB columns.
    const value = node?.getAttribute(name);
    return getNullableNumber(value);
};

// Frame SVG analysis looks for a real clipping/mask region before the frame is saved to database.
const FRAME_MASK_KEYWORD_REGEX = /(mask|clip|photo|image|placeholder|frame|khung|anh|ảnh)/i;

const getSvgMetaText = (node) => {
    if (!node) return "";
    return [node.getAttribute("id"), node.getAttribute("class"), node.getAttribute("name"), node.getAttribute("data-name"), node.getAttribute("inkscape:label"), node.getAttribute("aria-label"), node.querySelector("title")?.textContent]
        .filter(Boolean)
        .join(" ");
};

const isSvgNodeHidden = (node) => {
    const display = node.getAttribute("display");
    const visibility = node.getAttribute("visibility");
    const opacity = node.getAttribute("opacity");
    const style = node.getAttribute("style") || "";

    return display === "none" || visibility === "hidden" || opacity === "0" || /display\s*:\s*none/i.test(style) || /visibility\s*:\s*hidden/i.test(style) || /opacity\s*:\s*0/i.test(style);
};

const getSvgPolygonPath = (node) => {
    const points = node?.getAttribute("points") || "";
    const values = points
        .trim()
        .split(/[\s,]+/)
        .map((value) => Number(value));

    if (values.length < 4 || values.length % 2 !== 0 || values.some((value) => Number.isNaN(value))) return "";

    const pairs = [];
    for (let i = 0; i < values.length; i += 2) {
        pairs.push(`${values[i]} ${values[i + 1]}`);
    }

    const isPolygon = node.tagName.toLowerCase() === "polygon";
    return `M${pairs[0]} ${pairs.slice(1).map((point) => `L${point}`).join(" ")}${isPolygon ? " Z" : ""}`;
};

const getSvgEllipsePath = (node) => {
    // Circle/ellipse masks are converted to path so the editor and render API can clip by the same metadata.
    if (!node) return "";
    const tagName = node.tagName.toLowerCase();
    const cx = getSvgNumberAttribute(node, "cx") || 0;
    const cy = getSvgNumberAttribute(node, "cy") || 0;
    const rx = tagName === "circle" ? getSvgNumberAttribute(node, "r") : getSvgNumberAttribute(node, "rx");
    const ry = tagName === "circle" ? getSvgNumberAttribute(node, "r") : getSvgNumberAttribute(node, "ry");
    if (!rx || !ry) return "";
    return `M${cx - rx},${cy} A${rx},${ry} 0 1,0 ${cx + rx},${cy} A${rx},${ry} 0 1,0 ${cx - rx},${cy} Z`;
};
const analyzeFrameSvgContent = (svgText) => {
    // Client-side validation is intentionally heuristic so admin can decide whether an extra mask SVG is needed.
    if (!svgText || !svgText.trim()) {
        return { status: "error", hasPhotoArea: false, message: "Cannot read SVG content." };
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, "image/svg+xml");
    const parseError = doc.querySelector("parsererror");
    const svg = doc.querySelector("svg");

    if (parseError || !svg) {
        return { status: "error", hasPhotoArea: false, message: "Invalid SVG file." };
    }

    const viewBox = svg.getAttribute("viewBox") || "";
    const parsedViewBox = parseFrameViewBox(viewBox);
    const maskNodes = Array.from(doc.querySelectorAll("mask, clipPath")).filter((node) => !isSvgNodeHidden(node));
    const visibleShapeNodes = Array.from(doc.querySelectorAll("rect, path, ellipse, circle, polygon, polyline")).filter((node) => !isSvgNodeHidden(node));
    const labelledNodes = visibleShapeNodes.filter((node) => FRAME_MASK_KEYWORD_REGEX.test(getSvgMetaText(node)));
    // Shape extraction fills MaskType, MaskPath, and rect columns automatically from uploaded mask SVG.
    const primaryPath = visibleShapeNodes.find((node) => node.tagName.toLowerCase() === "path" && !isEmptyValue(node.getAttribute("d")));
    const primaryPolygonPath = getSvgPolygonPath(visibleShapeNodes.find((node) => ["polygon", "polyline"].includes(node.tagName.toLowerCase())));
    const primaryEllipsePath = getSvgEllipsePath(visibleShapeNodes.find((node) => ["circle", "ellipse"].includes(node.tagName.toLowerCase())));
    const primaryRect = visibleShapeNodes.find((node) => node.tagName.toLowerCase() === "rect" && getSvgNumberAttribute(node, "width") !== null && getSvgNumberAttribute(node, "height") !== null);
    const maskPath = primaryPath?.getAttribute("d") || primaryPolygonPath || primaryEllipsePath;
    const maskType = maskPath ? "path" : primaryRect ? "rect" : "";
    const maskRect = primaryRect
        ? {
              x: getSvgNumberAttribute(primaryRect, "x") || 0,
              y: getSvgNumberAttribute(primaryRect, "y") || 0,
              width: getSvgNumberAttribute(primaryRect, "width"),
              height: getSvgNumberAttribute(primaryRect, "height"),
          }
        : null;
    const hasPhotoArea = maskNodes.length > 0 || labelledNodes.length > 0 || !!maskType;

    return {
        status: hasPhotoArea ? "success" : "warning",
        hasPhotoArea,
        message: hasPhotoArea ? "SVG has a mask/clip or a detected photo area." : "No mask area detected. Without a Mask SVG, the image will use the full overlay bounds.",
        viewBox,
        maskType,
        maskPath,
        maskRect,
        maskXRate: maskRect && parsedViewBox?.width ? maskRect.x / parsedViewBox.width : null,
        maskYRate: maskRect && parsedViewBox?.height ? maskRect.y / parsedViewBox.height : null,
        maskWidthRate: maskRect && parsedViewBox?.width ? maskRect.width / parsedViewBox.width : null,
        maskHeightRate: maskRect && parsedViewBox?.height ? maskRect.height / parsedViewBox.height : null,
        maskCount: maskNodes.length,
        candidateCount: labelledNodes.length,
    };
};
const readSvgFile = (file) => {
    // FileReader lets the modal inspect local SVG before uploading it to storage.
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result || "");
        reader.onerror = reject;
        reader.readAsText(file);
    });
};

const readSvgUrl = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Cannot read current SVG file.");
    return await response.text();
};

const hasFrameViewBox = (frameModel = {}) => getNullableNumber(frameModel.viewBoxWidth) > 0 && getNullableNumber(frameModel.viewBoxHeight) > 0;

const buildFrameAnalysisChange = (analysis = {}, frameModel = {}, includeViewBox = false, preferAnalysis = false) => {
    const parsedViewBox = parseFrameViewBox(analysis.viewBox || "");
    const maskRect = analysis.maskRect || {};
    return {
        ...(includeViewBox && parsedViewBox
            ? {
                  viewBoxX: parsedViewBox.x,
                  viewBoxY: parsedViewBox.y,
                  viewBoxWidth: parsedViewBox.width,
                  viewBoxHeight: parsedViewBox.height,
              }
            : {}),
        // Frame modal defaults to rect when neither upload analysis nor user input provides a mask type.
        maskType: preferAnalysis ? analysis.maskType || frameModel.maskType || DEFAULT_FRAME_MASK_TYPE : frameModel.maskType || analysis.maskType || DEFAULT_FRAME_MASK_TYPE,
        maskPath: analysis.maskPath || frameModel.maskPath || "",
        maskX: preferAnalysis ? maskRect.x ?? frameModel.maskX ?? null : frameModel.maskX ?? maskRect.x ?? null,
        maskY: preferAnalysis ? maskRect.y ?? frameModel.maskY ?? null : frameModel.maskY ?? maskRect.y ?? null,
        maskWidth: preferAnalysis ? maskRect.width ?? frameModel.maskWidth ?? null : frameModel.maskWidth ?? maskRect.width ?? null,
        maskHeight: preferAnalysis ? maskRect.height ?? frameModel.maskHeight ?? null : frameModel.maskHeight ?? maskRect.height ?? null,
        maskXRate: preferAnalysis ? analysis.maskXRate ?? frameModel.maskXRate ?? null : frameModel.maskXRate ?? analysis.maskXRate ?? null,
        maskYRate: preferAnalysis ? analysis.maskYRate ?? frameModel.maskYRate ?? null : frameModel.maskYRate ?? analysis.maskYRate ?? null,
        maskWidthRate: preferAnalysis ? analysis.maskWidthRate ?? frameModel.maskWidthRate ?? null : frameModel.maskWidthRate ?? analysis.maskWidthRate ?? null,
        maskHeightRate: preferAnalysis ? analysis.maskHeightRate ?? frameModel.maskHeightRate ?? null : frameModel.maskHeightRate ?? analysis.maskHeightRate ?? null,
    };
};

const getUploadedImageUrl = async (file) => {
    const res = await Request.UploadImage(file, IMAGEABLE_TYPE.StockPhoto);
    const imageUrl = res?.data?.imageUrl || "";
    if (isEmptyValue(imageUrl)) throw new Error("Upload không trả về imageUrl.");
    return imageUrl;
};
const parseFrameViewBox = (viewBox = "") => {
    // ViewBox values are saved into DB columns, so parse them once before calling the API.
    const values = String(viewBox)
        .trim()
        .split(/[\s,]+/)
        .map((value) => Number(value));

    if (values.length < 4 || values.some((value) => Number.isNaN(value))) return null;
    return {
        x: values[0],
        y: values[1],
        width: values[2],
        height: values[3],
    };
};

const getDefaultFrameName = (frameModel = {}) => {
    // Admin can skip the name field; the uploaded file name becomes the default frame name.
    const fileName = frameModel.overlayFileName || frameModel.maskFileName || "Customize frame";
    return fileName.replace(/\.[^/.]+$/, "") || "Customize frame";
};

const getOverlayMaskPayload = (overlayUrl = "") => ({
    // No uploaded mask means the overlay SVG itself is used as the alpha mask and visual overlay.
    maskType: "path",
    maskX: null,
    maskY: null,
    maskWidth: null,
    maskHeight: null,
    maskXRate: null,
    maskYRate: null,
    maskWidthRate: null,
    maskHeightRate: null,
    maskPath: "",
    maskSvgUrl: overlayUrl,
});

const getFullRectMaskPayload = (viewBox = {}) => ({
    // Full rect is only a fallback for explicit rect masks that fail to provide geometry.
    maskType: DEFAULT_FRAME_MASK_TYPE,
    maskX: getRequiredNumber(viewBox.x),
    maskY: getRequiredNumber(viewBox.y),
    maskWidth: getRequiredNumber(viewBox.width),
    maskHeight: getRequiredNumber(viewBox.height),
    maskXRate: 0,
    maskYRate: 0,
    maskWidthRate: 1,
    maskHeightRate: 1,
    maskPath: "",
    maskSvgUrl: "",
});

const buildUploadedMaskPayload = (frameModel = {}, maskAnalysis = {}, viewBox = {}) => {
    // Uploaded mask metadata wins; unparsed mask SVGs still clip through maskSvgUrl instead of becoming full rect.
    const maskRect = maskAnalysis.maskRect || {};
    const maskType = maskAnalysis.maskType || (frameModel.maskSvgUrl ? "path" : frameModel.maskType || DEFAULT_FRAME_MASK_TYPE);
    const maskPath = maskAnalysis.maskPath || frameModel.maskPath || "";
    if (maskType === "path") {
        return {
            maskType: "path",
            maskX: null,
            maskY: null,
            maskWidth: null,
            maskHeight: null,
            maskXRate: null,
            maskYRate: null,
            maskWidthRate: null,
            maskHeightRate: null,
            maskPath,
            maskSvgUrl: frameModel.maskSvgUrl || "",
        };
    }

    const fullRect = getFullRectMaskPayload(viewBox);
    return {
        ...fullRect,
        maskSvgUrl: frameModel.maskSvgUrl || "",
        maskX: getNullableNumber(frameModel.maskX ?? maskRect.x) ?? fullRect.maskX,
        maskY: getNullableNumber(frameModel.maskY ?? maskRect.y) ?? fullRect.maskY,
        maskWidth: getNullableNumber(frameModel.maskWidth ?? maskRect.width) ?? fullRect.maskWidth,
        maskHeight: getNullableNumber(frameModel.maskHeight ?? maskRect.height) ?? fullRect.maskHeight,
        maskXRate: getNullableNumber(frameModel.maskXRate ?? maskAnalysis.maskXRate) ?? fullRect.maskXRate,
        maskYRate: getNullableNumber(frameModel.maskYRate ?? maskAnalysis.maskYRate) ?? fullRect.maskYRate,
        maskWidthRate: getNullableNumber(frameModel.maskWidthRate ?? maskAnalysis.maskWidthRate) ?? fullRect.maskWidthRate,
        maskHeightRate: getNullableNumber(frameModel.maskHeightRate ?? maskAnalysis.maskHeightRate) ?? fullRect.maskHeightRate,
    };
};
const buildCustomizeFramePayload = (frameModel = {}) => {
    // The API expects CUS_CustomizeFrameAssetDTO; this maps modal state into that DTO shape.
    if (isEmptyValue(frameModel.overlayUrl)) return { error: "Please upload Overlay SVG first." };

    const viewBox = parseFrameViewBox(frameModel.overlayAnalysis?.viewBox || frameModel.maskAnalysis?.viewBox || "");
    if (!viewBox || viewBox.width <= 0 || viewBox.height <= 0) return { error: "Please upload/check a valid SVG viewBox before saving." };

    const hasUploadedMask = !isEmptyValue(frameModel.maskSvgUrl);
    const maskPayload = hasUploadedMask ? buildUploadedMaskPayload(frameModel, frameModel.maskAnalysis || {}, viewBox) : getOverlayMaskPayload(frameModel.overlayUrl);
    return {
        data: {
            id: 0,
            assetGuid: frameModel.assetGuid || createFrameAssetGuid(),
            name: frameModel.name || getDefaultFrameName(frameModel),
            description: frameModel.description || "Created from admin frame upload.",
            thumbUrl: frameModel.overlayUrl,
            overlayUrl: frameModel.overlayUrl,
            blobUrl: "",
            viewBoxX: viewBox.x,
            viewBoxY: viewBox.y,
            viewBoxWidth: viewBox.width,
            viewBoxHeight: viewBox.height,
            ...maskPayload,
            isActive: true,
        },
    };
};
const buildUpdateCustomizeFramePayload = (frameAsset = {}) => {
    const payload = {
        id: getRequiredNumber(frameAsset.id),
        assetGuid: frameAsset.assetGuid || null,
        name: frameAsset.name || "",
        description: frameAsset.description || "",
        thumbUrl: frameAsset.thumbUrl || "",
        overlayUrl: frameAsset.overlayUrl || "",
        blobUrl: frameAsset.blobUrl || "",
        maskType: frameAsset.maskType || DEFAULT_FRAME_MASK_TYPE,
        maskPath: frameAsset.maskPath || "",
        maskSvgUrl: frameAsset.maskSvgUrl || "",
        isActive: !!frameAsset.isActive,
        cusImageId: getRequiredNumber(frameAsset.cusImageId),
    };

    requiredFrameNumberFields.forEach((field) => {
        payload[field] = getRequiredNumber(frameAsset[field]);
    });

    nullableFrameNumberFields.forEach((field) => {
        payload[field] = getNullableNumber(frameAsset[field]);
    });

    // Rect rows with no saved mask geometry should become an explicit full-frame rect on update.
    if (payload.maskType === DEFAULT_FRAME_MASK_TYPE && !payload.maskWidth && !payload.maskWidthRate) {
        Object.assign(payload, getFullRectMaskPayload({ x: payload.viewBoxX, y: payload.viewBoxY, width: payload.viewBoxWidth, height: payload.viewBoxHeight }), { maskSvgUrl: payload.maskSvgUrl || "" });
    }

    return payload;
};

function StockPhoto(props) {
    const [isBusy, setIsBusy] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [msg, setMsg] = useState("Đang tải...");
    const [datas, setDatas] = useState([StockPhotoModel]);
    const [paging, setPaging] = useState({ ...INITPAGING });
    const [isShowModal, setIsShowModal] = useState(false);
    const [isShowModalManyAdd, setIsShowModalManyAdd] = useState(false);
    // Frame upload modal state is separate from stock photo create/edit modals.
    const [isShowFrameUploadModal, setIsShowFrameUploadModal] = useState(false);
    const [editFrameId, setEditFrameId] = useState(0);
    // Active tab keeps pagination/filter requests aligned with the selected table.
    const [activeTab, setActiveTab] = useState(StockPhotoPageTab.StockPhoto);
    const [selectId, setSelectId] = useState(0);

    useEffect(() => {
        _getData({ ...INITPAGING, pageNumber: 1, pageSize: 100 }, StockPhotoPageTab.StockPhoto);
    }, []);

    async function _getData(paging, tab = activeTab) {
        setIsBusy(true);
        // Each tab owns its API so CommonTable paging always fetches the active data type.
        let [err, data] = tab === StockPhotoPageTab.CustomizeFrame ? await CusStockphotoServices.GetCustomizeFrameByPage(paging) : await CustomizeServices.GetStockPhotosPaging(paging);
        if (!err && data) {
            setDatas(data.listObjects || []);
            setPaging(data.paging || paging);
        } else {
            setDatas([]);
            setPaging(paging);
        }
        setIsBusy(false);
    }

    const _handleChangeTab = (tab) => {
        // Switching tabs resets selection and paging before loading the new data source.
        setActiveTab(tab);
        setSelectId(0);
        _getData({ ...INITPAGING, pageNumber: 1, pageSize: 100 }, tab);
    };

    const _handleChecked = (index, value) => {
        let newDatas = [...datas];
        if (value) setSelectId(newDatas[index].id);
        else setSelectId(0);
    };

    const _handleDelete = async (id) => {
        ConfirmDialog("Xác nhận xóa?", "Xin vui lòng xác nhận để xóa ", () => {
            return new Promise(async (resolve, reject) => {
                setIsLoading(true);
                setMsg("Đang xóa dữ liệu...");
                let [error, data] = await CustomizeServices.DeleteStockPhoto(id);
                if (!error && data) {
                    _getData(paging, activeTab);
                    resolve({ title: "Thành công", msg: "Xóa thành công." });
                } else {
                    Notify(NOTIFY.WARNING, "Có lỗi", "Có lỗi xảy ra trong quá trình xóa.");
                    reject({ title: "Thất bại", msg: "Vui lòng thử lại sau." });
                }
                setMsg(null);
                setIsLoading(false);
            });
        });
    };
    const _handleSetUnactiveFrame = async (id) => {
        // Customize frame action marks the selected frame inactive and refreshes the frame tab.
        ConfirmDialog("Xác nhận ẩn frame?", "Frame sẽ không còn hiển thị khi lấy danh sách frame đang active.", () => {
            return new Promise(async (resolve, reject) => {
                setIsBusy(true);
                let [error, data] = await CusStockphotoServices.SetUnactiveFrame(id);
                if (!error && data) {
                    _getData(paging, StockPhotoPageTab.CustomizeFrame);
                    resolve({ title: "Thành công", msg: "Ẩn frame thành công." });
                } else {
                    Notify(NOTIFY.WARNING, "Có lỗi", "Có lỗi xảy ra trong quá trình ẩn frame.");
                    reject({ title: "Thất bại", msg: "Vui lòng thử lại sau." });
                }
                setIsBusy(false);
            });
        });
    };

    function _renderTab() {
        // Tabs filter the page between legacy stock photos and customize frame assets.
        const tabs = [
            { key: StockPhotoPageTab.StockPhoto, label: "Họa tiết" },
            { key: StockPhotoPageTab.CustomizeFrame, label: "Frame ảnh" },
            { key: StockPhotoPageTab.EmbroideryPattern, label: "Họa tiết thêu" },
        ];
        return (
            <ul className="nav nav-tabs tabs-bordered">
                {tabs.map((tab) => (
                    <li key={tab.key} className="nav-item">
                        <a
                            className={activeTab === tab.key ? "nav-link active" : "nav-link btn-light"}
                            onClick={() => {
                                if (activeTab !== tab.key) _handleChangeTab(tab.key);
                            }}
                        >
                            <span>{tab.label}</span>
                        </a>
                    </li>
                ))}
            </ul>
        );
    }
    function _renderHeader() {
        // Customize frame tab uses frame metadata columns instead of stock photo columns.
        if (activeTab === StockPhotoPageTab.CustomizeFrame) {
            return (
                <thead>
                    <tr>
                        <th className="center" width={`12%`}>
                            Name
                        </th>
                        <th className="center" width={`14%`}>
                            Preview
                        </th>
                        <th className="center" width={`14%`}>
                            Mask Preview
                        </th>
                        <th className="center" width={`18%`}>
                            Description
                        </th>
                        <th className="center" width={`16%`}>
                            ViewBox
                        </th>
                        <th className="center" width={`16%`}>
                            Mask
                        </th>
                        <th className="center" width={`10%`}>
                            Hành động
                        </th>
                    </tr>
                </thead>
            );
        }

        return (
            <thead>
                <tr>
                    <th className="center" width={`15%`}></th>
                    <th className="center" width={`10%`}>
                        Title
                    </th>
                    <th className="center" width={`15%`}>
                        Thumbnail
                    </th>
                    <th className="center" width={`15%`}>
                        Folder
                    </th>
                    <th className="center" width={`15%`}>
                        Thông tin
                    </th>
                    <th className="center" width={`15%`}>
                        Tag
                    </th>
                    <th className="center" width={`15%`}>
                        Hành động
                    </th>
                </tr>
            </thead>
        );
    }

    function _renderAction() {
        let dataChecked = null;
        let isEnabled = false;
        if (datas && datas.length > 0) {
            dataChecked = datas.filter((x) => x.selected);
            if (dataChecked && dataChecked.length == 1) isEnabled = true;
        }
        // Action buttons follow the active tab so frame upload and stock photo delete stay separate.
        return (
            <React.Fragment>
                {activeTab === StockPhotoPageTab.StockPhoto && (
                    <button
                        className="btn btn-custom ml-3"
                        onClick={() => {
                            setIsShowModalManyAdd(true);
                        }}
                    >
                        Thêm mới hàng loạt
                    </button>
                )}
                {activeTab === StockPhotoPageTab.CustomizeFrame && (
                    <button
                        className="btn btn-outline-primary ml-3"
                        onClick={() => {
                            setEditFrameId(0);
                            setIsShowFrameUploadModal(true);
                        }}
                    >
                        Upload frame
                    </button>
                )}
                {activeTab === StockPhotoPageTab.StockPhoto && (
                    <button className={selectId > 0 ? "btn btn-danger ml-3" : "btn btn-danger ml-3 disable"} onClick={() => (selectId > 0 ? _handleDelete(selectId) : {})}>
                        Xóa
                    </button>
                )}
            </React.Fragment>
        );
    }

    function _renderBody() {
        // Frame rows show SVG preview and mask metadata returned from GetCustomizeFrameByPage.
        if (activeTab === StockPhotoPageTab.CustomizeFrame) {
            return (
                <tbody>
                    {datas && datas.length > 0 ? (
                        datas.map((item, idx) => {
                            const previewUrl = item.thumbUrl || item.overlayUrl;
                            // Mask preview is shown separately so admins can verify the clipping asset.
                            const maskPreviewUrl = item.maskSvgUrl;
                            return (
                                <tr key={item.id || idx}>
                                    <td>{item.name}</td>
                                    <td className="center frame-preview-cell">{previewUrl ? <img src={previewUrl} alt={item.name} /> : "-"}</td>
                                    <td className="center frame-preview-cell">{maskPreviewUrl ? <img src={maskPreviewUrl} alt={`${item.name} mask`} /> : "-"}</td>
                                    <td>{item.description}</td>
                                    <td>
                                        X: {item.viewBoxX} <br />
                                        Y: {item.viewBoxY} <br />
                                        W: {item.viewBoxWidth} <br />
                                        H: {item.viewBoxHeight}
                                    </td>
                                    <td>
                                        Type: {item.maskType || "-"} <br />
                                        X: {item.maskX ?? "-"} <br />
                                        Y: {item.maskY ?? "-"} <br />
                                        W: {item.maskWidth ?? "-"} <br />
                                        H: {item.maskHeight ?? "-"}
                                    </td>
                                    <td className="center">
                                        <button
                                            className="btn btn-custom btn-sm mr-2"
                                            onClick={() => {
                                                setEditFrameId(item.id);
                                                setIsShowFrameUploadModal(true);
                                            }}
                                        >
                                            Edit
                                        </button>
                                        {/* Frame action only appears in the customize frame tab and calls SetUnactiveFrame. */}
                                        <button className={item.isActive ? "btn btn-danger btn-sm" : "btn btn-danger btn-sm disable"} onClick={() => (item.isActive ? _handleSetUnactiveFrame(item.id) : {})}>
                                            Ẩn
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={999}>Không có dữ liệu</td>
                        </tr>
                    )}
                </tbody>
            );
        }

        return (
            <tbody>
                {datas && datas.length > 0 ? (
                    datas.map((item, idx) => {
                        return (
                            <tr key={idx}>
                                <td>
                                    <CheckBox id={idx.toString()} name={idx.toString()} checked={item.id ? item.id == selectId : false} label=" " onCheckedChange={(name, value) => _handleChecked(idx, value)} />
                                </td>
                                <td>{item.title}</td>
                                <td className="center">
                                    <img src={item.thumbUrl} alt={item.title} />
                                </td>
                                <td>
                                    <div dangerouslySetInnerHTML={{ __html: item.folderConnectionString }}></div>
                                </td>
                                <td>
                                    Width: {item.width}px <br />
                                    Height: {item.height}px <br />
                                    <span className={`text-center badge ${item.isActive ? "badge-secondary" : "badge-danger"} my-2`}>{item.isActive ? `Hiện` : `Ẩn`}</span>
                                </td>
                                <td>
                                    {item.tagConnections &&
                                        item.tagConnections.map((ele, i) => {
                                            return (
                                                <span className="text-center badge badge-secondary m-2" key={i}>
                                                    {ele}
                                                </span>
                                            );
                                        })}
                                </td>
                                <td>
                                    <button
                                        className="btn btn-custom"
                                        onClick={() => {
                                            setIsShowModal(true);
                                            setSelectId(item.id);
                                        }}
                                    >
                                        Chi tiết
                                    </button>
                                </td>
                            </tr>
                        );
                    })
                ) : (
                    <tr>
                        <td colSpan={999}>Không có dữ liệu</td>
                    </tr>
                )}
            </tbody>
        );
    }

    if (activeTab === StockPhotoPageTab.EmbroideryPattern) {
        return (
            <Wrapper>
                {_renderTab()}
                <EmbroideryPatternList {...props} />
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <Loading show={isBusy} msg="Đang tải dữ liệu..." />
            <Breadcrumbs currentPage="Quản lý Stock Photo" />
            {_renderTab()}
            <CommonTable datas={datas} paging={paging} searchBasic onFilter={(paging) => _getData(paging, activeTab)} renderAction={() => _renderAction()} renderHeader={() => _renderHeader()} renderBody={() => _renderBody()} />
            {isShowModal && (
                <AddOrEditStockModal
                    isShowModal={isShowModal}
                    onClose={() => {
                        setIsShowModal(false);
                        setSelectId(0);
                    }}
                    onSaveContinue={() => {
                        setIsShowModal(false);
                        _getData({ ...paging });
                    }}
                    id={selectId}
                />
            )}
            {isShowModalManyAdd && (
                <ManyAddStockModal
                    isShowModal={isShowModalManyAdd}
                    onClose={() => {
                        setIsShowModalManyAdd(false);
                    }}
                    onSaveContinue={() => {
                        setIsShowModalManyAdd(false);
                        _getData({ ...paging });
                    }}
                />
            )}
            {/* Frame upload modal is rendered on demand so normal stock photo loading stays unchanged. */}
            {isShowFrameUploadModal && (
                <FrameUploadModal
                    isShowModal={isShowFrameUploadModal}
                    frameId={editFrameId}
                    onClose={() => {
                        setIsShowFrameUploadModal(false);
                        setEditFrameId(0);
                    }}
                    onSaveContinue={() => {
                        setIsShowFrameUploadModal(false);
                        setEditFrameId(0);
                        _getData({ ...paging }, StockPhotoPageTab.CustomizeFrame);
                    }}
                />
            )}
        </Wrapper>
    );
}

// FrameUploadModal owns the overlay/mask upload workflow for customize frame assets.
function FrameUploadModal({ isShowModal, frameId = 0, onClose, onSaveContinue }) {
    const [frameModel, setFrameModel] = useState({ ...CustomizeFrameAssetModel, ...FrameUploadModel });
    const [isChecking, setIsChecking] = useState(false);
    const isEditMode = frameId > 0;

    useEffect(() => {
        if (isEditMode) _getFrameAsset(frameId);
        else setFrameModel({ ...CustomizeFrameAssetModel, ...FrameUploadModel });
    }, [frameId]);

    const _getFrameAsset = async (id) => {
        setIsChecking(true);
        let [err, data] = await StockFolderServices.GetCustomizeFrameAssetDTO(id);
        if (!err && data) {
            // Existing frame rows open as rect by default, while SVG conversion still receives the raw DB mask type.
            const loadedFrame = { ...CustomizeFrameAssetModel, ...FrameUploadModel, ...data, maskType: data.maskType || DEFAULT_FRAME_MASK_TYPE };
            setFrameModel(loadedFrame);
            await _convertExistingFrameSvg({ ...loadedFrame, maskType: data.maskType || "" });
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Load frame failed.");
        }
        setIsChecking(false);
    };

    const _convertExistingFrameSvg = async (loadedFrame) => {
        const shouldConvertMask = isEmptyValue(loadedFrame.maskType) || (loadedFrame.maskType === "path" && isEmptyValue(loadedFrame.maskPath));
        const shouldConvertViewBox = !hasFrameViewBox(loadedFrame);
        if (!shouldConvertMask && !shouldConvertViewBox) return;

        const svgUrl = loadedFrame.maskSvgUrl || loadedFrame.overlayUrl;
        if (isEmptyValue(svgUrl)) return;

        try {
            const svgText = await readSvgUrl(svgUrl);
            const analysis = analyzeFrameSvgContent(svgText);
            const analysisKey = loadedFrame.maskSvgUrl ? "maskAnalysis" : "overlayAnalysis";
            setFrameModel((prev) => ({
                ...prev,
                [analysisKey]: analysis,
                ...buildFrameAnalysisChange(analysis, loadedFrame, shouldConvertViewBox),
            }));
        } catch (error) {
            Notify(NOTIFY.WARNING, "Frame", error?.message || "Cannot convert current SVG.");
        }
    };

    const _updateFrameModel = (change) => {
        setFrameModel((prev) => ({ ...prev, ...change }));
    };

    const _renderInput = (field, placeholder, type = "text") => (
        <input
            className="form-control"
            type={type}
            value={frameModel[field] ?? ""}
            placeholder={placeholder}
            onChange={(e) => {
                _updateFrameModel({ [field]: e.target.value });
            }}
        />
    );

    const _renderNumberInput = (field, placeholder) => _renderInput(field, placeholder, "number");

    const _uploadSvgFile = async (e, target) => {
        // Cache the input before awaits because React may clear the event target after async work.
        const input = e.target;
        const file = input?.files && input.files[0];
        if (!file) return;

        try {
            setIsChecking(true);
            const svgText = await readSvgFile(file);
            const analysis = analyzeFrameSvgContent(svgText);
            const uploadedUrl = await getUploadedImageUrl(file);
            const isOverlay = target === "overlay";
            const parsedViewBox = parseFrameViewBox(analysis.viewBox || "");

            _updateFrameModel(
                isOverlay
                    ? {
                          overlayUrl: uploadedUrl,
                          thumbUrl: uploadedUrl,
                          overlayFileName: file.name,
                          overlayAnalysis: analysis,
                          ...(parsedViewBox
                              ? {
                                    viewBoxX: parsedViewBox.x,
                                    viewBoxY: parsedViewBox.y,
                                    viewBoxWidth: parsedViewBox.width,
                                    viewBoxHeight: parsedViewBox.height,
                                }
                              : {}),
                      }
                    : {
                          maskSvgUrl: uploadedUrl,
                          maskFileName: file.name,
                          maskAnalysis: analysis,
                          ...buildFrameAnalysisChange(analysis, frameModel, false, true),
                      }
            );

        } catch (error) {
            Notify(NOTIFY.ERROR, "Lỗi upload", error?.message || "Không thể upload SVG.");
        } finally {
            if (input) input.value = "";
            setIsChecking(false);
        }
    };

    const _handleSaveFrame = async () => {
        if (isEditMode) {
            const payload = buildUpdateCustomizeFramePayload(frameModel);
            if (!payload.id) {
                Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Frame id is invalid.");
                return;
            }
            if (isEmptyValue(payload.name)) {
                Notify(NOTIFY.WARNING, "Frame", "Frame name is required.");
                return;
            }

            let shouldClose = false;
            try {
                setIsChecking(true);
                let [err, data] = await StockFolderServices.UpdateCustomizeFrameAssetDTO(payload);
                if (!err && data) {
                    Notify(NOTIFY.SUCCESS, NOTIFY.SUCCESS, "Update frame successfully.");
                    shouldClose = true;
                } else {
                    Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Update frame failed.");
                }
            } finally {
                setIsChecking(false);
                if (shouldClose) onSaveContinue ? onSaveContinue() : onClose();
            }
            return;
        }

        // Save button posts the validated frame DTO to CreateUpdateCustomizeFrame.
        const payloadResult = buildCustomizeFramePayload(frameModel);
        if (payloadResult.error) {
            Notify(NOTIFY.WARNING, "Frame", payloadResult.error);
            return;
        }

        let shouldClose = false;
        try {
            setIsChecking(true);
            let [err, data] = await CusStockphotoServices.CreateUpdateCustomizeFrame(payloadResult.data);
            if (!err && data) {
                Notify(NOTIFY.SUCCESS, NOTIFY.SUCCESS, "Save frame successfully.");
                shouldClose = true;
            } else {
                Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Save frame failed.");
            }
        } finally {
            setIsChecking(false);
            if (shouldClose) onSaveContinue ? onSaveContinue() : onClose();
        }
    };
    const _renderAnalysis = (analysis) => {
        if (!analysis) return null;

        return (
            <div className={`frame-analysis ${analysis.status}`}>
                <div>{analysis.message}</div>
                {analysis.viewBox && <div>ViewBox: {analysis.viewBox}</div>}
                {analysis.maskType && <div>Mask type: {analysis.maskType}</div>}
                {analysis.maskCount !== undefined && <div>Mask/clipPath: {analysis.maskCount}</div>}
                {analysis.candidateCount !== undefined && <div>Layer nghi là vùng ảnh: {analysis.candidateCount}</div>}
            </div>
        );
    };

    return (
        <CommonModal show={isShowModal} size="lg" id="frame-upload-modal" hiddenFooter isBusy={isChecking} busyMsg="Processing..." onClose={onClose} title={isEditMode ? "Edit frame" : "Upload frame"}>
            <FrameUploadWrapper>
                <div className="frame-meta-grid">
                    {/* Frame metadata fields provide the required DTO values before saving to API. */}
                    <input
                        className="form-control"
                        value={frameModel.name ?? ""}
                        placeholder="Frame name"
                        onChange={(e) => {
                            _updateFrameModel({ name: e.target.value });
                        }}
                    />
                    <select
                        className="form-control"
                        value={frameModel.maskType ?? ""}
                        onChange={(e) => {
                            _updateFrameModel({ maskType: e.target.value });
                        }}
                    >
                        <option value="rect">Rect (default)</option>
                        <option value="path">Path</option>
                    </select>
                    <textarea
                        className="form-control"
                        value={frameModel.description ?? ""}
                        placeholder="Description"
                        onChange={(e) => {
                            _updateFrameModel({ description: e.target.value });
                        }}
                    />
                </div>
                {isEditMode && (
                    <div className="frame-edit-grid">
                        <label className="frame-active-toggle">
                            <input
                                type="checkbox"
                                checked={!!frameModel.isActive}
                                onChange={(e) => {
                                    _updateFrameModel({ isActive: e.target.checked });
                                }}
                            />
                            Active
                        </label>
                        <span className="asset-guid">AssetGuid: {frameModel.assetGuid || "-"}</span>
                        {_renderInput("thumbUrl", "Thumb URL")}
                        {_renderInput("overlayUrl", "Overlay URL")}
                        {_renderInput("maskSvgUrl", "Mask SVG URL")}
                        {_renderInput("blobUrl", "Blob URL")}
                        {_renderNumberInput("viewBoxX", "ViewBox X")}
                        {_renderNumberInput("viewBoxY", "ViewBox Y")}
                        {_renderNumberInput("viewBoxWidth", "ViewBox width")}
                        {_renderNumberInput("viewBoxHeight", "ViewBox height")}
                        {_renderNumberInput("maskX", "Mask X")}
                        {_renderNumberInput("maskY", "Mask Y")}
                        {_renderNumberInput("maskWidth", "Mask width")}
                        {_renderNumberInput("maskHeight", "Mask height")}
                        {_renderNumberInput("maskXRate", "Mask X rate")}
                        {_renderNumberInput("maskYRate", "Mask Y rate")}
                        {_renderNumberInput("maskWidthRate", "Mask width rate")}
                        {_renderNumberInput("maskHeightRate", "Mask height rate")}
                        <textarea
                            className="form-control frame-edit-wide"
                            rows={4}
                            value={frameModel.maskPath ?? ""}
                            placeholder="Mask path"
                            onChange={(e) => {
                                _updateFrameModel({ maskPath: e.target.value });
                            }}
                        />
                    </div>
                )}
                <div className="frame-upload-grid">
                    <div className="frame-upload-panel">
                        <h4>Overlay SVG</h4>
                        <p>Overlay là SVG client sẽ đọc để kiểm tra frame có vùng ảnh thật sự hay chưa.</p>
                        <input
                            type="file"
                            accept=".svg,image/svg+xml"
                            onChange={(e) => {
                                _uploadSvgFile(e, "overlay");
                            }}
                        />
                        {frameModel.overlayFileName && <div className="file-name">{frameModel.overlayFileName}</div>}
                        {_renderAnalysis(frameModel.overlayAnalysis)}
                        {frameModel.overlayUrl && (
                            <div className="preview-box">
                                <img src={frameModel.overlayUrl} alt="Overlay preview" />
                            </div>
                        )}
                    </div>
                    <div className="frame-upload-panel">
                        <h4>Mask SVG</h4>
                        <p>Nếu overlay không có mask/clipPath, upload file mask để xác định vùng ảnh.</p>
                        <input
                            type="file"
                            accept=".svg,image/svg+xml"
                            onChange={(e) => {
                                _uploadSvgFile(e, "mask");
                            }}
                        />
                        {frameModel.maskFileName && <div className="file-name">{frameModel.maskFileName}</div>}
                        {_renderAnalysis(frameModel.maskAnalysis)}
                        {frameModel.maskSvgUrl && (
                            <div className="preview-box mask-preview">
                                <img src={frameModel.maskSvgUrl} alt="Mask preview" />
                            </div>
                        )}
                    </div>
                    <div className="frame-upload-note">
                        {/* Upload notes clarify the role of each SVG so admins can prepare frame files correctly. */}
                        <h4>Note</h4>
                        <ul>
                            <li>
                                <strong>Overlay SVG</strong> là phần khung hiển thị phía trên ảnh.
                            </li>
                            <li>
                                <strong>Mask SVG</strong> là vùng ảnh sẽ được cắt vào bên trong frame.
                            </li>
                            <li>Nếu overlay đã có mask/clipPath hoặc vùng ảnh rõ ràng, có thể để Mask SVG trống.</li>
                        </ul>
                    </div>
                </div>
                {frameModel.overlayAnalysis && !frameModel.maskSvgUrl && (
                    <div className="frame-warning">Không upload Mask SVG: Overlay SVG sẽ được dùng làm mask. Nếu overlay không có vùng alpha đúng, hãy upload Mask SVG riêng.</div>
                )}
                <div className="frame-save-actions">
                    {/* Save action persists the frame asset after overlay/mask validation has passed. */}
                    <button
                        className="btn btn-custom"
                        disabled={isChecking}
                        onClick={() => {
                            _handleSaveFrame();
                        }}
                    >
                        Save frame
                    </button>
                    <button className="btn btn-inverse ml-2" disabled={isChecking} onClick={onClose}>
                        Close
                    </button>
                </div>
            </FrameUploadWrapper>
        </CommonModal>
    );
}
export default StockPhoto;

const Wrapper = styled.div`
    .modal-class-body {
        min-height: 50vh;
    }
    td img {
        width: 80px;
        height: 80px;
        object-fit: contain;
    }
    /* Frame preview cells use a dark background so transparent SVG overlays and masks stay visible. */
    .frame-preview-cell {
        background: #0101;
        color: #f9fafb;
    }

    .frame-preview-cell img {
        border-radius: 4px;
    }
`;

// FrameUploadWrapper keeps the modal layout readable without affecting the stock photo table.
const FrameUploadWrapper = styled.div`
    .frame-meta-grid {
        /* Top metadata row now has only name and mask type after SortOrder was removed. */
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(160px, 220px);
        gap: 12px;
        margin-bottom: 16px;
    }

    .frame-meta-grid textarea {
        grid-column: 1 / -1;
        min-height: 72px;
        resize: vertical;
    }

    .frame-edit-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
        margin-bottom: 16px;
    }

    .frame-active-toggle {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 0;
    }

    .asset-guid {
        color: #6b7280;
        word-break: break-all;
    }

    .frame-edit-wide {
        grid-column: 1 / -1;
    }

    .frame-upload-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr)) minmax(220px, 0.8fr);
        gap: 16px;
    }

    .frame-upload-panel,
    .frame-upload-note {
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 16px;
        background: #fff;
    }

    .frame-upload-panel h4,
    .frame-upload-note h4 {
        margin-bottom: 8px;
        font-weight: 600;
    }

    .frame-upload-panel p {
        min-height: 40px;
        margin-bottom: 12px;
        color: #6b7280;
    }

    .frame-upload-note ul {
        margin: 0;
        padding-left: 18px;
        color: #374151;
        line-height: 1.6;
    }

    .frame-upload-note li + li {
        margin-top: 8px;
    }
    .file-name {
        margin-top: 8px;
        color: #374151;
        font-size: 13px;
    }

    .frame-analysis {
        margin-top: 12px;
        padding: 10px;
        border-radius: 6px;
        font-size: 13px;
        line-height: 1.5;
    }

    .frame-analysis.success {
        border: 1px solid #86efac;
        background: #f0fdf4;
        color: #166534;
    }

    .frame-analysis.warning {
        border: 1px solid #fde68a;
        background: #fffbeb;
        color: #92400e;
    }

    .frame-analysis.error {
        border: 1px solid #fecaca;
        background: #fef2f2;
        color: #991b1b;
    }

    .preview-box {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 180px;
        margin-top: 12px;
        border: 1px dashed #d1d5db;
        border-radius: 8px;
        background: #f9fafb;
    }

    .preview-box img {
        max-width: 100%;
        max-height: 220px;
        object-fit: contain;
    }

    .mask-preview {
        background-image: linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%);
        background-position:
            0 0,
            0 10px,
            10px -10px,
            -10px 0;
        background-size: 20px 20px;
    }

    .frame-warning {
        margin-top: 16px;
        padding: 12px;
        border-radius: 6px;
        background: #fef2f2;
        color: #991b1b;
        font-weight: 600;
    }

    .frame-save-actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 16px;
    }

    @media (max-width: 768px) {
        .frame-meta-grid,
        .frame-edit-grid,
        .frame-upload-grid {
            grid-template-columns: 1fr;
        }
    }
`;
